// `one` is format 1 (implied consent); `three` is format 3 (explicit opt-out).
export const US_BANNER_VARIANTS = {
    OPT_OUT: 'one',
    OPT_IN: 'three',
};

export const US_BANNER_MODES = {
    DEFAULT: 'default',
    OPT_OUT_ALL: 'optOutAll',
    OPT_IN_ALL: 'optInAll',
    CUSTOM: 'custom',
};

const DEFAULTS = 'defaults';
const US_STATE_CODE = /^us-[a-z]{2}$/;
const VARIANTS = Object.values(US_BANNER_VARIANTS);

const isVariant = variant => VARIANTS.includes(variant);

const isUsJurisdiction = code => code === 'us' || US_STATE_CODE.test(code);

const stateEntries = usVariantMapping =>
    Object.entries(usVariantMapping || {}).filter(
        ([code, variant]) => US_STATE_CODE.test(code) && isVariant(variant)
    );

// The country-wide `us` key is dropped, not migrated — `defaults` replaced it.
export const normalizeUsVariantMapping = usVariantMapping => {
    const mapping = Object.fromEntries(stateEntries(usVariantMapping));
    const defaults = usVariantMapping?.[DEFAULTS];
    return isVariant(defaults) ? { ...mapping, [DEFAULTS]: defaults } : mapping;
};

const statesForVariant = (usVariantMapping, variant) =>
    stateEntries(usVariantMapping)
        .filter(([, entry]) => entry === variant)
        .map(([code]) => code);

export const getUsBannerOptInStates = usVariantMapping =>
    statesForVariant(usVariantMapping, US_BANNER_VARIANTS.OPT_IN);

// The form has no control for these; only the API can set them.
export const getUsBannerOptOutStates = usVariantMapping =>
    statesForVariant(usVariantMapping, US_BANNER_VARIANTS.OPT_OUT);

export const makeUsVariantMapping = (mode, optInStates = [], currentMapping) => {
    switch (mode) {
        case US_BANNER_MODES.OPT_OUT_ALL:
            return { [DEFAULTS]: US_BANNER_VARIANTS.OPT_OUT };
        case US_BANNER_MODES.OPT_IN_ALL:
            return { [DEFAULTS]: US_BANNER_VARIANTS.OPT_IN };
        case US_BANNER_MODES.CUSTOM: {
            const optIn = (optInStates || []).filter(code => US_STATE_CODE.test(code));
            // Without this, adding one state in the picker would delete the rest.
            const unrepresented = getUsBannerOptOutStates(currentMapping).filter(
                code => !optIn.includes(code)
            );
            const entries = [
                ...unrepresented.map(code => [code, US_BANNER_VARIANTS.OPT_OUT]),
                ...optIn.map(code => [code, US_BANNER_VARIANTS.OPT_IN]),
            ];
            // `defaults` makes Custom exhaustive, so it stops inheriting new legislated states.
            // An empty selection stays `{}` — `{defaults}` alone reads as Opt-out-All.
            return entries.length
                ? Object.fromEntries([...entries, [DEFAULTS, US_BANNER_VARIANTS.OPT_OUT]])
                : {};
        }
        default:
            return {};
    }
};

// `usBannerOverride` is form-only: "Default" and "custom, nothing picked" are both `{}`.
export const getUsBannerMode = ({ usVariantMapping, usBannerOverride } = {}) => {
    if (!usBannerOverride) {
        return US_BANNER_MODES.DEFAULT;
    }
    if (!stateEntries(usVariantMapping).length) {
        const defaults = usVariantMapping?.[DEFAULTS];
        if (defaults === US_BANNER_VARIANTS.OPT_OUT) {
            return US_BANNER_MODES.OPT_OUT_ALL;
        }
        if (defaults === US_BANNER_VARIANTS.OPT_IN) {
            return US_BANNER_MODES.OPT_IN_ALL;
        }
    }
    return US_BANNER_MODES.CUSTOM;
};

export const hasMissingOptInStates = formValues =>
    getUsBannerMode(formValues) === US_BANNER_MODES.CUSTOM &&
    !stateEntries(formValues?.usVariantMapping).length;

export const toUsBannerFormValues = ({ usVariantMapping, ccpaRelaxed } = {}) => {
    const mapping = normalizeUsVariantMapping(usVariantMapping);
    if (Object.keys(mapping).length) {
        return { usVariantMapping: mapping, usBannerOverride: true };
    }
    return ccpaRelaxed
        ? {
              usVariantMapping: makeUsVariantMapping(US_BANNER_MODES.OPT_OUT_ALL),
              usBannerOverride: true,
          }
        : { usVariantMapping: {}, usBannerOverride: false };
};

// Mirrors the server's rewrite so anything still reading ccpaRelaxed agrees.
export const toCcpaRelaxed = usVariantMapping =>
    !stateEntries(usVariantMapping).length &&
    usVariantMapping?.[DEFAULTS] === US_BANNER_VARIANTS.OPT_OUT;

// `undefined` = not covered; the caller falls through to ccpaRelaxed, then curated.
export const getUsVariantOverride = (usVariantMapping, jurisdiction) => {
    const code = String(jurisdiction || '').toLowerCase();
    if (!usVariantMapping || !isUsJurisdiction(code)) {
        return undefined;
    }
    const variant = usVariantMapping[code] ?? usVariantMapping[DEFAULTS];
    return isVariant(variant) ? variant : undefined;
};
